import IAmdTimeline from "models/IAmdTimeline";
import IAmdTimelineEvent from "models/IAmdTimelineEvent";
import ISagaState from "models/ISagaState";
import theme from "Theme/AppTheme";

export type TimelineChartEntry = [
    label: string,
    seconds: number,
    style: string,
];

export type TimelineChartData = [
    [string, string, { role: "style" }],
    ...TimelineChartEntry[]
];

type Interval = [start: number, end: number];
type ServiceRun = { start: number; firstEvent?: number; lastEvent?: number };

export const ProcessingStatus = "processing";

export function timelineEventsEqual(
    left: IAmdTimelineEvent[],
    right: IAmdTimelineEvent[],
) {
    return (
        left.length === right.length &&
        left.every((event, index) => {
            const other = right[index];
            return (
                event.microservice === other.microservice &&
                event.event === other.event &&
                event.time === other.time &&
                event.success === other.success &&
                event.isStartOfMicroservice === other.isStartOfMicroservice
            );
        })
    );
}

function getTimelineTimestamp(value: string) {
    const timestamp = new Date(value).getTime();
    const fraction = value.match(/\.(\d+)(?:Z|[+-]\d{2}:?\d{2})$/)?.[1];
    if (!Number.isFinite(timestamp) || !fraction || fraction.length <= 3)
        return timestamp;
    return timestamp + Number(fraction.slice(3, 6).padEnd(3, "0")) / 1000;
}

function getProcessingInterval(run: ServiceRun, end: number): Interval {
    const { firstEvent, lastEvent } = run;
    return firstEvent !== undefined &&
    lastEvent !== undefined &&
    lastEvent > firstEvent
        ? [firstEvent, lastEvent]
        : [run.start, end];
}

// Interpret service boundaries once for both the chart and pipeline totals.
export function getTimelineData(events: IAmdTimelineEvent[]) {
    const chronological = events
        .map((event) => ({
            ...event,
            timestamp: getTimelineTimestamp(event.time),
        }))
        .filter((event) => Number.isFinite(event.timestamp))
        .sort((left, right) => left.timestamp - right.timestamp);
    const active = new Map<string, ServiceRun[]>();
    const rows: TimelineChartEntry[] = [];
    const processingIntervals: Interval[] = [];

    chronological.forEach((event, index) => {
        if (event.isStartOfMicroservice) {
            const previous = chronological[index - 1];
            if (previous) {
                rows.push([
                    `${event.microservice} (Queue)`,
                    (event.timestamp - previous.timestamp) / 1000,
                    `color: ${theme.palette.blue.main}`,
                ]);
            }
            const runs = active.get(event.microservice) ?? [];
            runs.push({ start: event.timestamp });
            active.set(event.microservice, runs);
        } else if (event.event !== null) {
            const run = active.get(event.microservice)?.at(-1);
            if (run) {
                run.firstEvent ??= event.timestamp;
                run.lastEvent = event.timestamp;
            }
        } else {
            const run = active.get(event.microservice)?.shift();
            if (!run) return;
            const interval = getProcessingInterval(run, event.timestamp);
            processingIntervals.push(interval);
            rows.push([
                `${event.microservice} (${
                    event.success ? "Succeeded" : "Failed"
                })`,
                (interval[1] - interval[0]) / 1000,
                `color: ${
                    event.success
                        ? theme.palette.primary.main
                        : theme.palette.red.main
                }`,
            ]);
        }
    });

    const chartData: TimelineChartData = [
        ["Microservice", "Time Spent (s)", { role: "style" }],
        ...rows.reverse(),
    ];
    return {
        chartData,
        processingIntervals,
        activeRuns: Array.from(active.values()).flat(),
        start: chronological.at(0)?.timestamp,
        end: chronological.at(-1)?.timestamp,
    };
}

type TimelineData = ReturnType<typeof getTimelineData>;

export function calculatePipelineDurations(
    timeline: TimelineData,
    durationEnd: number,
) {
    if (timeline.start === undefined) return undefined;
    const pipelineStart = timeline.start;
    const pipelineEnd = Math.max(pipelineStart, durationEnd);
    const intervals = [
        ...timeline.processingIntervals,
        ...timeline.activeRuns.map((run) =>
            getProcessingInterval(run, pipelineEnd),
        ),
    ]
        .map(
            ([start, end]): Interval => [
                Math.max(pipelineStart, start),
                Math.min(pipelineEnd, Math.max(start, end)),
            ],
        )
        .filter(([start, end]) => end > start)
        .sort(([left], [right]) => left - right);

    let processing = 0;
    let processedThrough = pipelineStart;
    for (const [start, end] of intervals) {
        processing += Math.max(0, end - Math.max(start, processedThrough));
        processedThrough = Math.max(processedThrough, end);
    }
    const elapsed = pipelineEnd - pipelineStart;
    return { elapsed, processing, queueing: Math.max(0, elapsed - processing) };
}

function formatDuration(milliseconds: number) {
    const totalMinutes = Math.max(0, Math.floor(milliseconds / 60_000));
    if (totalMinutes < 1) return "< 1m";
    const days = Math.floor(totalMinutes / 1_440);
    const hours = Math.floor((totalMinutes % 1_440) / 60);
    const minutes = totalMinutes % 60;
    return [days && `${days}d`, hours && `${hours}h`, minutes && `${minutes}m`]
        .filter(Boolean)
        .join(" ");
}

export function formatServiceLevel(serviceLevel: string) {
    return serviceLevel === "Highest" ? "Fast Lane" : serviceLevel;
}

type PipelineSummary = {
    processingDuration: string;
    queueingDuration: string;
    rtsEstimate: string;
    rtsDetail: string;
    rtsProgress?: number;
    rtsTerminalStateColor?: string;
};

export function buildPipelineSummary(
    timeline: IAmdTimeline,
    data: TimelineData,
    sagas: ISagaState[] | undefined,
    now: number,
): PipelineSummary | undefined {
    const status = timeline.processingStatus.toLowerCase();
    if (
        data.end === undefined &&
        timeline.telemetryStatus !== "outsideLookupWindow"
    )
        return undefined;
    const durations = calculatePipelineDurations(
        data,
        status !== ProcessingStatus ? data.end ?? now : now,
    );
    const summary = {
        processingDuration: durations
            ? formatDuration(durations.processing)
            : "Not available",
        queueingDuration: durations
            ? formatDuration(durations.queueing)
            : "Not available",
    };

    switch (status) {
        case "readytosample":
        case "sampled":
            return {
                ...summary,
                rtsEstimate: "Ready",
                rtsDetail: durations
                    ? "The AMD has reached Ready to Sample."
                    : "The AMD reached Ready to Sample.",
                rtsProgress: 100,
            };
        case "failedtoprocess":
            return {
                ...summary,
                rtsEstimate: "Unavailable",
                rtsDetail: "The AMD failed before reaching Ready to Sample.",
                rtsTerminalStateColor: theme.palette.error.main,
            };
        case "notprocessed":
            return {
                ...summary,
                rtsEstimate: "Unavailable",
                rtsDetail: "This AMD will not progress to Ready to Sample.",
                rtsTerminalStateColor: theme.palette.warning.main,
            };
    }
    if (!durations) {
        return {
            ...summary,
            rtsEstimate: "Stuck Processing",
            rtsDetail:
                "The AMD has not reached an end state, and its telemetry is no longer available.",
        };
    }

    const saga = sagas?.find(
        (item) => item.sagaName.toLowerCase() === "ready to sample saga",
    );
    const requirements = saga?.sagaRequirementsState ?? [];
    const completed = requirements.filter(
        (item) => item.isMet || item.isNoLongerRequired,
    ).length;
    const progress = saga?.complete
        ? 1
        : requirements.length
            ? completed / requirements.length
            : undefined;
    if (!progress) {
        return {
            ...summary,
            rtsEstimate: "Calculating",
            rtsDetail:
                "Waiting for this AMD to complete an RTS requirement before estimating.",
            rtsProgress: progress,
        };
    }
    const remaining = Math.max(
        0,
        durations.elapsed * ((1 - progress) / progress),
    );
    const estimatedReadyAt = new Date(now + remaining).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
    const progressPercent = Math.min(100, Math.round(progress * 100));
    return {
        ...summary,
        rtsEstimate: `≈ ${formatDuration(remaining)} remaining`,
        rtsDetail: `${progressPercent}% of this AMD's RTS requirements complete · around ${estimatedReadyAt}`,
        rtsProgress: progressPercent,
    };
}
